import * as React from "react";
import { icons } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Blog } from "@/payload-types";
import { Badge, type BadgeVariant } from "@/components/frontend/ui/badge/badge";

export type BlogInfoData = Pick<Blog, "author" | "publishedAt">;
export type BlogInfoPart = "author" | "publishedAt" | "readingTime";

type BlogInfoPartConfig = {
	icon: keyof typeof icons;
	getText: (post: BlogInfoData) => string | null;
};

const publishedAtFormatter = new Intl.DateTimeFormat("nl-NL", {
	day: "numeric",
	month: "long",
	year: "numeric",
	timeZone: "Europe/Amsterdam",
});

const BLOG_INFO_PARTS: Record<Exclude<BlogInfoPart, "readingTime">, BlogInfoPartConfig> = {
	author: {
		icon: "User",
		getText: (post) => {
			const authorNames = post.author?.map((author) => (typeof author === "object" && author ? author.name : null)).filter((name): name is string => Boolean(name)) ?? [];
			return authorNames.length > 0 ? authorNames.join(", ") : null;
		},
	},
	publishedAt: {
		icon: "CalendarDays",
		getText: (post) => (post.publishedAt ? publishedAtFormatter.format(new Date(post.publishedAt)) : null),
	},
};

interface BlogInfoProps {
	post: BlogInfoData;
	parts?: BlogInfoPart[];
	className?: string;
	readingTimeLabel?: string | null;
	badgeVariant?: BadgeVariant;
}

export function BlogInfo({ post, parts = ["author", "publishedAt"], className, readingTimeLabel, badgeVariant }: BlogInfoProps) {
	const items = parts
		.map((part) => {
			if (part === "readingTime") return readingTimeLabel ? { part, text: readingTimeLabel, icon: "Clock3" as const } : null;
			const { icon, getText } = BLOG_INFO_PARTS[part];
			const text = getText(post);
			return text ? { part, text, icon } : null;
		})
		.filter((item) => item !== null);

	if (items.length === 0) return null;

	return (
		<div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1 text-sm", className)}>
			{items.map((item, index) => {
				if (badgeVariant) {
					return (
						<Badge key={item.part} variant={badgeVariant} iconBefore={item.icon}>
							{item.text}
						</Badge>
					);
				}

				const Icon = icons[item.icon];
				return (
					<React.Fragment key={item.part}>
						<span className="inline-flex items-center gap-1">
							<Icon className="size-4" />
							<span>{item.text}</span>
						</span>
						{index < items.length - 1 && <span aria-hidden="true">|</span>}
					</React.Fragment>
				);
			})}
		</div>
	);
}
